<script setup>
import ModelIcon from '@/components/icons/ModelIcon.vue';
import { i18n } from '@/i18n';
import { Image as ImageIcon, Check } from 'lucide-vue-next';
import Tooltip from '@/components/Tooltip.vue';
import CreditIcon from '@/components/icons/Credit.vue';

const props = defineProps({
  model: Object,
  modelValue: String,
});

const emit = defineEmits(['click']);

function selectModel(item) {
  emit('click', item);
}

function handleModelMouseEnter(event) {
  if (props.modelValue !== props.model.value) {
    event.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.05)';
  }
}

function handleModelMouseLeave(event) {
  if (props.modelValue !== props.model.value) {
    event.currentTarget.style.backgroundColor = '';
  }
}
</script>

<template>
  <button
    :key="model.value"
    @click="selectModel(model)"
    class="group w-full text-left transition-all duration-200 flex justify-start p-2 rounded-lg active:scale-[0.98] mb-0.5"
    :style="props.modelValue === model.value ? 'background-color: rgba(0, 0, 0, 0.05)' : ''"
    @mouseenter="handleModelMouseEnter"
    @mouseleave="handleModelMouseLeave"
  >
    <div class="seede-model-item flex items-center gap-3 w-full">
      <!-- Model Icon -->
      <div
        class="shrink-0 size-10 flex items-center justify-center rounded-lg transition-all duration-200 bg-white border border-black/5"
      >
        <ModelIcon :type="model.icon" class="size-5 text-blue-600" />
      </div>

      <!-- Model Content -->
      <div class="flex flex-col flex-1 min-w-0 gap-1">
        <!-- Header Row -->
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-1 min-w-0">
            <span
              class="seede-model-name text-sm font-normal truncate"
              :class="props.modelValue === model.value ? 'text-black' : 'text-black/50'"
            >
              {{ model.label }}
            </span>

            <!-- Badges -->
            <div class="seede-model-meta flex items-center gap-1 shrink-0">
              <!-- Credits Badge -->
              <div
                :class="[
                  'inline-flex items-center pl-1 pr-2 py-0.5 rounded-full text-xs font-medium transition-all duration-200',
                  model.creditsCost === 1 ? 'bg-black/5 text-blue-500' : 'bg-black/5 text-blue-500',
                ]"
              >
                <CreditIcon
                  :class="['h-3 w-3', model.creditsCost === 1 ? 'text-blue-500' : 'text-blue-500']"
                />
                <span>{{ model.creditsCost }}</span>
              </div>

              <!-- Multimodal Badge -->
              <Tooltip v-if="model.supportImage" :text="$t('create.multimodal')">
                <div
                  class="inline-flex items-center px-1.5 py-1 rounded-full text-xs font-medium transition-all duration-200 bg-black/5 text-black"
                >
                  <ImageIcon class="h-3 w-3 text-indigo-500" />
                </div>
              </Tooltip>

              <!-- New Badge -->
              <div
                v-if="model.isNew"
                class="inline-flex items-center pl-1 pr-2 py-0.5 rounded-full text-xs font-medium transition-all duration-200 bg-emerald-100 text-emerald-700 border border-emerald-200"
              >
                <span>{{ $t('modelSelector.new') || 'NEW' }}</span>
              </div>

              <!-- Free Badge -->
              <div
                v-if="model.isFree"
                class="inline-flex items-center pl-1 pr-2 py-0.5 rounded-full text-xs font-medium transition-all duration-200 bg-gradient-to-r from-rose-50 to-pink-50 text-rose-600 border border-rose-200/60 shadow-sm"
              >
                <span>{{ $t('modelSelector.limitedTimeFree') || 'Limited Time Free' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Description -->
        <p
          class="seede-model-description text-xs font-normal truncate"
          :class="props.modelValue === model.value ? 'text-black' : 'text-black/50'"
        >
          {{
            i18n.isChinese
              ? model.descriptions.zh
              : model.descriptions[i18n.locale] || model.descriptions.en
          }}
        </p>
      </div>

      <!-- Check Icon -->
      <div v-if="props.modelValue === model.value" class="shrink-0 ml-2">
        <Check class="size-5 text-black/60" />
      </div>
    </div>
  </button>
</template>
